import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  useSidebar,
} from '@shared/ui/sidebar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@shared/ui/collapsible';
import { SuiteNavItem } from '@suites/manifest';
import { useAuth } from '@shared/auth/AuthContext';
import { cn } from '@shared/lib/utils';
import { SidebarNavItem } from './SidebarNavItem';

export interface SidebarNavGroupProps {
  groupName?: string;
  items: SuiteNavItem[];
  isOpen?: boolean;
  onToggle?: () => void;
}

export const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  groupName,
  items,
  isOpen = true,
  onToggle,
}) => {
  const { hasPermission, hasModule } = useAuth();
  const { state } = useSidebar();
  const isSidebarCollapsed = state === 'collapsed';

  const visibleItems = items.filter((item) => {
    if (item.permission && typeof hasPermission === 'function' && !hasPermission(item.permission)) return false;
    if (item.module && typeof hasModule === 'function' && !hasModule(item.module)) return false;
    return true;
  });

  if (visibleItems.length === 0) return null;

  const effectiveOpen = isSidebarCollapsed ? true : isOpen;

  const menu = (
    <SidebarMenu>
      {visibleItems.map((item) => (
        <SidebarNavItem key={item.path} item={item} />
      ))}
    </SidebarMenu>
  );

  if (!groupName) {
    return <SidebarGroup className="py-1">{menu}</SidebarGroup>;
  }

  return (
    <SidebarGroup className="py-0.5">
      <Collapsible open={effectiveOpen} onOpenChange={onToggle}>
        <SidebarGroupLabel asChild className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-2">
          <CollapsibleTrigger
            type="button"
            className="flex w-full items-center justify-between py-1 px-1.5 rounded-md hover:bg-sidebar-accent/50 cursor-pointer transition-colors group select-none"
          >
            <span className="truncate">{groupName}</span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 shrink-0 transition-transform duration-200 text-muted-foreground group-hover:text-sidebar-foreground',
                effectiveOpen && 'rotate-180'
              )}
            />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
          {menu}
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
};
